// U1c-3 — SLink edge-agent provisioning API module (over the U1c-2 backend:
// POST/GET /api/v1/admin/slink-agents, gated MANAGE_DEVICES, effective-tenant).
// Mirrors users.ts house style: a const-as base key, list keyed on the active
// tenant, mutations invalidate the base key.
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { useTenantStore } from "@/store/tenant";

// Mirrors the U1c-2 backend SlinkAgentListItem (app/slink_agents/schemas.py) —
// TOKEN-FREE: the list/read surface never carries enrollment-token material.
export interface SlinkAgentListItem {
  id: string;
  name: string;
  site_id: string;
  tenant_id: string;
  status: string;
  token_version: number;
  credential_type: string;
  created_at: string;
}

// Mirrors SlinkAgentCreated — the create response, which ALSO carries the
// one-time enrollment_token. That field is surfaced once by the caller in
// ephemeral state and is NEVER persisted, logged, or put in a query cache.
export interface SlinkAgentCreated extends SlinkAgentListItem {
  enrollment_token: string;
}

export interface SlinkAgentCreateInput {
  name: string;
  site_id: string;
  credential_type?: string;
}

const agentsKey = ["slink-agents"] as const;

// List — keyed on the active tenant (mirror users.ts:31) so a super-admin's
// tenant switch re-scopes the list cleanly without a page refresh, not relying
// solely on the global resetQueries the TenantSwitcher fires. The backend
// returns a plain array (list[SlinkAgentListItem]).
export function useSlinkAgents() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: [...agentsKey, "list", tenantId],
    queryFn: async () => {
      const { data } = await api.get<SlinkAgentListItem[]>(
        "/api/v1/admin/slink-agents",
      );
      return data;
    },
  });
}

// Create + first-issue the enrollment token. The plaintext token comes back
// ONCE in the response; the caller renders it in ephemeral component state and
// never persists it.
export function useCreateSlinkAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SlinkAgentCreateInput) => {
      const { data } = await api.post<SlinkAgentCreated>(
        "/api/v1/admin/slink-agents",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: agentsKey }),
  });
}
